import { useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { Upload, Loader2 } from 'lucide-react';
import { RichTextEditor } from './RichTextEditor';
import { mailTemplateImagesService } from '../../services/mail-template-images.service';
import { themeLocksColors, type EmailThemeId } from './emailTheme';

interface BlockEditorProps {
  block: any;
  onChange: (updated: any) => void;
  theme?: EmailThemeId;
}

const ACCEPTED_IMAGE_TYPES = 'image/jpeg,image/png,image/webp,image/gif';

function ImageUploadButton({ onUploaded }: { onUploaded: (url: string) => void }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const handleSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }
    e.target.value = '';
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Imagem excede 5MB');
      return;
    }
    setUploading(true);
    try {
      const result = await mailTemplateImagesService.upload(file);
      onUploaded(result.url);
      toast.success('Imagem enviada');
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Falha ao enviar imagem';
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_IMAGE_TYPES}
        onChange={handleSelect}
        className="hidden"
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-info border border-info/40 rounded-lg hover:bg-info/10 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {uploading ? (
          <>
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            Enviando...
          </>
        ) : (
          <>
            <Upload className="w-3.5 h-3.5" />
            Enviar imagem
          </>
        )}
      </button>
    </>
  );
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return <label className="block text-xs font-medium text-gray-600 mb-1">{children}</label>;
}

function TextInput({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <input
      type="text"
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
    />
  );
}

function ColorPicker({
  value,
  onChange,
  label,
}: {
  value: string;
  onChange: (v: string) => void;
  label: string;
}) {
  return (
    <div className="flex items-center gap-2">
      <input
        type="color"
        value={value || '#000000'}
        onChange={(e) => onChange(e.target.value)}
        className="w-8 h-8 rounded border border-gray-200 cursor-pointer p-0.5"
      />
      <span className="text-xs text-gray-500">{label}: </span>
      <code className="text-xs font-mono text-gray-700">{value || '#000000'}</code>
    </div>
  );
}

function BrandColorNote() {
  return (
    <p className="text-xs text-ink-tertiary">
      No tema da DocuGen, a cor segue a identidade visual da marca.
    </p>
  );
}

export function BlockEditor({ block, onChange, theme = 'default' }: BlockEditorProps) {
  const update = (field: string, value: any) => {
    onChange({ ...block, [field]: value });
  };
  const colorsLocked = themeLocksColors(theme);
  const isDocugen = theme === 'docugen';

  if (!block) {
    return (
      <div className="flex flex-col items-center justify-center h-32 text-gray-400">
        <p className="text-sm">Selecione um bloco para editar</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <h3 className="text-sm font-semibold text-gray-700 capitalize">
        Editar bloco: <span className="text-blue-600">{block.type}</span>
      </h3>

      {block.type === 'header' && (
        <>
          <div>
            <FieldLabel>Título</FieldLabel>
            <TextInput
              value={block.title}
              onChange={(v) => update('title', v)}
              placeholder="Título do e-mail"
            />
          </div>
          <div>
            <FieldLabel>{isDocugen ? 'Imagem de topo (hero)' : 'URL do Logo'}</FieldLabel>
            <div className="flex items-center gap-2 mb-2">
              <ImageUploadButton onUploaded={(url) => update('logoUrl', url)} />
              {block.logoUrl && (
                <button
                  type="button"
                  onClick={() => update('logoUrl', '')}
                  className="text-xs text-ink-tertiary hover:text-danger transition-colors"
                >
                  Remover
                </button>
              )}
            </div>
            <TextInput
              value={block.logoUrl}
              onChange={(v) => update('logoUrl', v)}
              placeholder={isDocugen ? 'https://.../hero.png' : 'https://exemplo.com/logo.png'}
            />
            {isDocugen && (
              <p className="text-xs text-ink-tertiary mt-1.5">
                Com imagem, ela ocupa o topo inteiro (600px) e o título vira o texto alternativo.
                Sem imagem, o topo padrão da DocuGen mostra o logo e o título abaixo dele.
              </p>
            )}
          </div>
        </>
      )}

      {block.type === 'window' && (
        <>
          <div>
            <FieldLabel>Título da janela</FieldLabel>
            <TextInput
              value={block.title}
              onChange={(v) => update('title', v)}
              placeholder="CHANGELOG.TXT"
            />
          </div>
          <div>
            <FieldLabel>Conteúdo</FieldLabel>
            <RichTextEditor
              value={block.content}
              onChange={(v) => update('content', v)}
              placeholder="Texto dentro da janela... Use {{nome}}, {{empresa}}, etc."
            />
          </div>
          <div>
            <FieldLabel>Terminal (opcional)</FieldLabel>
            <textarea
              value={block.terminal || ''}
              onChange={(e) => update('terminal', e.target.value)}
              rows={4}
              placeholder={'visual: atualizado\nrecursos: os mesmos'}
              className="w-full px-3 py-2 text-sm font-mono border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="text-xs text-ink-tertiary mt-1">
              Uma linha por item, no formato <code>rótulo: valor</code>. Vira{' '}
              <code>&gt; rótulo ..... valor</code> com os valores alinhados.
            </p>
          </div>
        </>
      )}

      {block.type === 'text' && (
        <div>
          <FieldLabel>Conteúdo</FieldLabel>
          <RichTextEditor
            value={block.content}
            onChange={(v) => update('content', v)}
            placeholder="Digite o texto do bloco... Use {{nome}}, {{empresa}}, etc."
          />
          <p className="text-xs text-gray-400 mt-1">
            Dica: use as variáveis {`{{nome}}`}, {`{{empresa}}`}, etc. dentro do texto.
          </p>
        </div>
      )}

      {block.type === 'subtitle' && (
        <div>
          <FieldLabel>Subtítulo (H2)</FieldLabel>
          <TextInput
            value={block.text}
            onChange={(v) => update('text', v)}
            placeholder="Texto do subtítulo"
          />
          <div className="mt-3">
            <FieldLabel>Cor</FieldLabel>
            {colorsLocked ? (
              <BrandColorNote />
            ) : (
              <ColorPicker
                value={block.color || '#111827'}
                onChange={(v) => update('color', v)}
                label="Cor"
              />
            )}
          </div>
        </div>
      )}

      {block.type === 'image' && (
        <>
          <div>
            <FieldLabel>Imagem</FieldLabel>
            <div className="flex items-center gap-2">
              <ImageUploadButton onUploaded={(url) => update('url', url)} />
              {block.url && (
                <button
                  type="button"
                  onClick={() => update('url', '')}
                  className="text-xs text-ink-tertiary hover:text-danger transition-colors"
                >
                  Remover
                </button>
              )}
            </div>
            <p className="text-xs text-ink-tertiary mt-1.5">JPG, PNG, WEBP ou GIF até 5MB.</p>
          </div>
          <div>
            <FieldLabel>URL da Imagem (ou cole um link)</FieldLabel>
            <TextInput
              value={block.url}
              onChange={(v) => update('url', v)}
              placeholder="https://exemplo.com/imagem.jpg"
            />
          </div>
          <div>
            <FieldLabel>Texto Alternativo (alt)</FieldLabel>
            <TextInput
              value={block.alt}
              onChange={(v) => update('alt', v)}
              placeholder="Descrição da imagem"
            />
          </div>
          {block.url && (
            <div className="mt-1">
              <img
                src={block.url}
                alt={block.alt || ''}
                className="max-w-full h-auto rounded border border-border max-h-24 object-contain"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            </div>
          )}
        </>
      )}

      {block.type === 'cta' && (
        <>
          <div>
            <FieldLabel>Texto do Botão</FieldLabel>
            <TextInput
              value={block.text}
              onChange={(v) => update('text', v)}
              placeholder="Clique aqui"
            />
          </div>
          <div>
            <FieldLabel>URL de Destino</FieldLabel>
            <TextInput
              value={block.url}
              onChange={(v) => update('url', v)}
              placeholder="https://exemplo.com/destino"
            />
          </div>
          <div>
            <FieldLabel>Cor do Botão</FieldLabel>
            {colorsLocked ? (
              <BrandColorNote />
            ) : (
              <ColorPicker value={block.color} onChange={(v) => update('color', v)} label="Cor" />
            )}
          </div>
        </>
      )}

      {block.type === 'divider' && colorsLocked && (
        <div>
          <FieldLabel>Divisor</FieldLabel>
          <p className="text-xs text-ink-tertiary">
            No tema da DocuGen, o divisor é tracejado em navy, com cor e espessura fixas.
          </p>
        </div>
      )}

      {block.type === 'divider' && !colorsLocked && (
        <>
          <div>
            <FieldLabel>Cor do Divisor</FieldLabel>
            <ColorPicker value={block.color} onChange={(v) => update('color', v)} label="Cor" />
          </div>
          <div>
            <FieldLabel>Espessura (px)</FieldLabel>
            <input
              type="number"
              min={1}
              max={10}
              value={block.thickness || 1}
              onChange={(e) => update('thickness', Number(e.target.value))}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </>
      )}

      {block.type === 'footer' && (
        <div>
          <FieldLabel>Conteúdo do Rodapé</FieldLabel>
          <RichTextEditor
            value={block.content}
            onChange={(v) => update('content', v)}
            placeholder="© 2024 Sua Empresa. Todos os direitos reservados."
          />
        </div>
      )}
    </div>
  );
}

export default BlockEditor;
